// ---------------------------------------------------------------------------
// Video Embed Resolver — shared course/page-builder contract
// ---------------------------------------------------------------------------
// Recommended providers get canonical embed URLs. Generic HTTPS URLs remain
// usable only as sandboxed embeds.

export interface VideoEmbed {
  provider: string;
  embedUrl: string;
  isSandboxed: boolean;
}

interface ProviderPattern {
  name: string;
  match: (url: URL) => boolean;
  extractEmbedUrl: (url: URL) => string | null;
}

const PROVIDERS: ProviderPattern[] = [
  {
    name: "youtube",
    match: (url) =>
      url.hostname === "www.youtube.com" || url.hostname === "youtube.com" || url.hostname === "youtu.be",
    extractEmbedUrl: (url) => {
      let videoId: string | null = null;
      if (url.hostname === "youtu.be") {
        videoId = url.pathname.slice(1);
      } else if (url.pathname.startsWith("/embed/")) {
        videoId = url.pathname.split("/").filter(Boolean)[1] ?? null;
      } else {
        videoId = url.searchParams.get("v");
      }
      return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null;
    },
  },
  {
    name: "vimeo",
    match: (url) =>
      url.hostname === "vimeo.com"
      || url.hostname === "www.vimeo.com"
      || url.hostname === "player.vimeo.com",
    extractEmbedUrl: (url) => {
      const pathParts = url.pathname.split("/").filter(Boolean);
      const videoId = url.hostname === "player.vimeo.com" && pathParts[0] === "video"
        ? pathParts[1]
        : pathParts[0];
      const privacyHash = url.searchParams.get("h");
      if (!videoId || !privacyHash) return null;
      const params = new URLSearchParams({ h: privacyHash });
      return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
    },
  },
  {
    name: "wistia",
    match: (url) => url.hostname.endsWith(".wistia.com"),
    extractEmbedUrl: (url) => {
      const match = url.pathname.match(/\/medias\/([a-z0-9]+)/);
      return match ? `https://fast.wistia.net/embed/iframe/${match[1]}` : null;
    },
  },
  {
    name: "bunny",
    match: (url) => url.hostname.endsWith(".b-cdn.net") || url.hostname.includes("iframe.mediadelivery.net"),
    extractEmbedUrl: (url) => url.href,
  },
  {
    name: "loom",
    match: (url) => url.hostname === "www.loom.com" || url.hostname === "loom.com",
    extractEmbedUrl: (url) => {
      const match = url.pathname.match(/\/share\/([a-f0-9]+)/);
      return match ? `https://www.loom.com/embed/${match[1]}` : null;
    },
  },
  {
    name: "cloudflare_stream",
    match: (url) => url.hostname === "watch.cloudflarestream.com" || url.hostname.endsWith(".cloudflarestream.com"),
    extractEmbedUrl: (url) => url.href,
  },
];

export function resolveVideoEmbed(videoUrl: string): VideoEmbed | null {
  let url: URL;
  try {
    url = new URL(videoUrl);
  } catch {
    return null;
  }

  if (url.protocol !== "https:") {
    return null;
  }

  for (const provider of PROVIDERS) {
    if (provider.match(url)) {
      const embedUrl = provider.extractEmbedUrl(url);
      if (!embedUrl) return null;
      return {
        provider: provider.name,
        embedUrl,
        isSandboxed: false,
      };
    }
  }

  return {
    provider: "generic",
    embedUrl: url.href,
    isSandboxed: true,
  };
}

export const RECOMMENDED_VIDEO_PROVIDERS = PROVIDERS.map((provider) => provider.name);
